import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PLAN_LIMITS } from "@currencycore/shared";
import type { KeyUsageResponse, PlanTier, UsageDay, UsageEndpoint, UsageStats } from "@currencycore/shared";
import {
  getMcpStatus,
  getOrgUsage,
  getOrgUsageDaily,
  getOrgUsageKeys,
  listApiKeys,
  type McpStatus,
} from "../lib/api.ts";
import { AreaChart } from "../components/Charts.tsx";
import { trackUsageView } from "../lib/analytics.ts";

function fmt(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPct(requests: number, limit: number) {
  if (limit === Number.POSITIVE_INFINITY || limit === 0) return 0;
  return Math.min(100, Math.round((requests / limit) * 100));
}

/** "2026-06-08" → "Jun 8" (UTC, matching how the series is bucketed). */
function dayLabel(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

type Range = 7 | 14 | 30;

export function UsagePage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [mcp, setMcp] = useState<McpStatus | null>(null);
  const [series, setSeries] = useState<UsageDay[] | null>(null);
  const [keyUsage, setKeyUsage] = useState<KeyUsageResponse | null>(null);
  const [keyCount, setKeyCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [range, setRange] = useState<Range>(30);
  // Per-key table: filter to a single endpoint type (or show the breakdown).
  const [endpointFilter, setEndpointFilter] = useState<UsageEndpoint | "all">("all");

  // Monthly totals + plan/limit (usage_counters) and the MCP subtotal — loaded once.
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    setLoading(true);
    setError(false);
    getOrgUsage(orgId)
      .then((u) => {
        if (active) { setUsage(u); trackUsageView(u.plan ?? "free"); }
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    getMcpStatus(orgId)
      .then((m) => {
        if (active) setMcp(m);
      })
      .catch(() => {
        /* MCP tile degrades to "—" */
      });
    listApiKeys(orgId)
      .then((ks) => {
        if (active) setKeyCount(ks.length);
      })
      .catch(() => {
        /* API-keys tile degrades to "—" */
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  // Real per-day series (usage_daily) for the selected window.
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    setSeries(null);
    getOrgUsageDaily(orgId, range)
      .then((s) => {
        if (active) setSeries(s.days);
      })
      .catch(() => {
        if (active) setSeries([]);
      });
    return () => {
      active = false;
    };
  }, [orgId, range]);

  // Per-key + per-endpoint usage for the current month (not linked to the day range).
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    setKeyUsage(null);
    getOrgUsageKeys(orgId)
      .then((k) => {
        if (active) setKeyUsage(k);
      })
      .catch(() => {
        if (active)
          setKeyUsage({
            organizationId: orgId,
            period: new Date().toISOString().slice(0, 7),
            keys: [],
            byEndpoint: { convert: 0, rates: 0, ppp: 0, ai: 0 },
          });
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  if (loading) {
    return (
      <div className="skeleton-page">
        <div className="skeleton" style={{ height: "2.5rem", width: "10rem", borderRadius: "8px" }} />
        <div className="grid grid-3" style={{ marginTop: "16px" }}>
          {[0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: "7rem" }} />)}
        </div>
        <div className="skeleton" style={{ height: "220px", marginTop: "16px" }} />
      </div>
    );
  }

  if (error || !usage) {
    return (
      <div className="animate-fade-in">
        <div className="page-head rise">
          <div>
            <h1>Usage</h1>
            <p className="sub">API traffic for this organization.</p>
          </div>
        </div>
        <div className="card card-pad rise">
          <p className="muted">Couldn't load usage right now. Refresh to try again.</p>
        </div>
      </div>
    );
  }

  const effLimit = usage.effectiveLimit; // base + active grant; null = unlimited
  const unlimited = effLimit === null;
  const pct = unlimited ? 0 : fmtPct(usage.requests, effLimit);
  const remaining = unlimited ? null : Math.max(0, effLimit - usage.requests);
  const meterClass = pct >= 90 ? "danger" : pct >= 70 ? "warn" : "";
  // Estimated over-usage this period (per 1,000 over the limit), from the plan's rate.
  const overageCents = usage.plan in PLAN_LIMITS ? PLAN_LIMITS[usage.plan as PlanTier].overagePer1000Cents : null;
  const overageUnits = !unlimited && overageCents != null ? Math.floor(Math.max(0, usage.requests - effLimit) / 1000) : 0;
  const overageEst = overageCents != null ? (overageUnits * overageCents) / 100 : 0;
  const period = usage.period ?? new Date().toISOString().slice(0, 7);

  const mcpRequests = mcp?.mcpRequests ?? null;
  const mcpShare =
    mcpRequests !== null && usage.requests > 0
      ? `${Math.min(100, Math.round((mcpRequests / usage.requests) * 100))}% of all requests`
      : "of this month's requests";

  // AI credits — a separate pool (monthly allocation + never-expiring purchased).
  const aiLimit = usage.aiCreditLimit; // null = unlimited/custom
  const aiUsed = usage.aiCreditsUsed;
  const aiRemaining = usage.aiCreditsRemaining; // null = unlimited
  const aiPct = aiLimit == null || aiLimit === 0 ? 0 : Math.min(100, Math.round((aiUsed / aiLimit) * 100));
  const aiMeterClass = aiPct >= 90 ? "danger" : aiPct >= 70 ? "warn" : "";
  const purchased = usage.purchasedAiCredits;
  // Team seats.
  const seatLimit = usage.seatLimit; // null = unlimited
  const seatsUsed = usage.seatsUsed;
  // Billing cycle window (from the subscription / Dodo), when known.
  const cycleStart = usage.currentPeriodStart;
  const cycleEnd = usage.currentPeriodEnd;

  const days = series ?? [];
  const values = days.map((d) => d.requests);
  const labels = days.map((d) => dayLabel(d.date));
  const windowTotal = sum(values);
  const windowMcp = sum(days.map((d) => d.mcpRequests));
  const windowAi = sum(days.map((d) => d.aiRequests));

  return (
    <div className="animate-fade-in">
      {/* Page header */}
      <div className="page-head rise">
        <div>
          <h1>Usage</h1>
          <p className="sub">
            Billing period <span className="mono">{period}</span>: API traffic for this organization.
          </p>
        </div>
        <div className="page-head-actions">
          <div className="segmented">
            {([7, 14, 30] as Range[]).map((r) => (
              <button key={r} className={range === r ? "active" : ""} onClick={() => setRange(r)}>
                {r}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stat tiles — every value is queried, none fabricated. */}
      <div className="grid grid-4 rise">
        <div className="card stat">
          <span className="k">Requests this month</span>
          <span className="v tnum">{fmt(usage.requests)}</span>
          <span className="meta">{remaining !== null ? `of ${fmt(effLimit!)} included` : "unlimited"}</span>
        </div>
        <div className="card stat">
          <span className="k">MCP requests</span>
          <span className="v tnum">{mcpRequests !== null ? fmt(mcpRequests) : "—"}</span>
          <span className="meta">{mcpShare}</span>
        </div>
        <div className="card stat">
          <span className="k">Team seats</span>
          <span className="v tnum">{seatLimit == null ? fmt(seatsUsed) : `${seatsUsed}/${seatLimit}`}</span>
          <span className="meta">{seatLimit == null ? "unlimited seats" : "members + pending invites"}</span>
        </div>
        <div className="card stat">
          <span className="k">API keys</span>
          <span className="v tnum">{keyCount !== null ? keyCount.toLocaleString() : "—"}</span>
          <span className="meta">active in this org</span>
        </div>
      </div>

      {/* Usage meter */}
      {!unlimited && (
        <div className="card card-pad mt rise">
          <div className="between" style={{ marginBottom: "12px" }}>
            <div className="row gap-sm">
              <strong style={{ fontSize: "14px" }}>Usage vs. limit</strong>
              <span className="badge badge-neutral">{usage.planLabel} · {fmt(effLimit!)} / mo</span>
              {usage.grantRequests > 0 && (
                <span className="badge badge-accent">incl. +{fmt(usage.grantRequests)} grant</span>
              )}
            </div>
            <strong className="tnum" style={{ fontSize: "14px" }}>{pct}%</strong>
          </div>
          <div className={`meter${meterClass ? ` ${meterClass}` : ""}`}>
            <span style={{ width: `${pct}%` }} />
          </div>
          <p className="muted" style={{ fontSize: "12.5px", marginTop: "10px" }}>
            {fmt(usage.requests)} of {fmt(effLimit!)} requests used.{" "}
            {pct >= 70 && overageUnits === 0 && (
              <>At current pace you'll reach the limit soon. <Link to={`/dashboard/${orgId}/billing`} style={{ color: "var(--accent-text)" }}>Upgrade →</Link></>
            )}
          </p>
          {overageUnits > 0 && (
            <p className="muted" style={{ fontSize: "12.5px", marginTop: "6px" }}>
              Over-usage this period: <strong style={{ color: "var(--text)" }}>~${overageEst.toFixed(2)}</strong>{" "}
              ({fmt(overageUnits)}×1,000 requests over your limit), billed to your subscription.
            </p>
          )}
        </div>
      )}

      {/* Hard-cap marker (read-only; managed on Billing) */}
      {usage.effectiveCap != null && (
        <p className="muted" style={{ fontSize: "12.5px", marginTop: "8px" }}>
          Hard cap: {fmt(usage.requests)} / {fmt(usage.effectiveCap)}. Requests are blocked beyond this.{" "}
          <Link to={`/dashboard/${orgId}/billing`} style={{ color: "var(--accent-text)" }}>Change limit →</Link>
        </p>
      )}

      {/* AI credits — a separate pool: monthly allocation + never-expiring purchased balance. */}
      <div className="card card-pad mt rise">
        <div className="between" style={{ marginBottom: "12px" }}>
          <div className="row gap-sm">
            <strong style={{ fontSize: "14px" }}>AI credits</strong>
            <span className="badge badge-neutral">{aiLimit == null ? "Unlimited monthly" : `${fmt(aiLimit)} / mo`}</span>
          </div>
          {aiLimit != null && <strong className="tnum" style={{ fontSize: "14px" }}>{aiPct}%</strong>}
        </div>

        {aiLimit != null ? (
          <>
            <div className={`meter${aiMeterClass ? ` ${aiMeterClass}` : ""}`}>
              <span style={{ width: `${aiPct}%` }} />
            </div>
            <p className="muted" style={{ fontSize: "12.5px", marginTop: "10px" }}>
              {fmt(aiUsed)} of {fmt(aiLimit)} monthly AI credits used · {fmt(aiRemaining ?? 0)} remaining.{" "}
              Purchased balance: <strong>{fmt(purchased)}</strong> credits (never expire).
            </p>
          </>
        ) : (
          <p className="muted" style={{ fontSize: "12.5px" }}>
            Unlimited monthly AI credits on this plan. Purchased balance:{" "}
            <strong>{fmt(purchased)}</strong> credits.
          </p>
        )}

        <Link to={`/dashboard/${orgId}/plans#ai-credits`} className="btn btn-primary" style={{ marginTop: "14px", width: "fit-content" }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
          Purchase credits
        </Link>
        <p className="muted" style={{ fontSize: "12px", marginTop: "8px" }}>
          <strong>1 AI credit = 1 AI request.</strong> One-time purchase. Credits never expire and aren't a subscription.{" "}
          <a
            href="https://currency-core.com/docs/ai-credits"
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--accent-text)" }}
          >
            Learn about AI credits →
          </a>
        </p>
      </div>

      {/* Daily API requests — real per-day series from usage_daily. */}
      <div className="card mt rise">
        <div className="card-head">
          <div className="row gap-sm">
            <h2>Daily API requests</h2>
            <span className="badge badge-neutral">last {range} days</span>
          </div>
          <span className="hint mono">
            total {fmt(windowTotal)}{windowMcp > 0 ? ` · ${fmt(windowMcp)} via MCP` : ""}{windowAi > 0 ? ` · ${fmt(windowAi)} via AI` : ""}
          </span>
        </div>
        <div className="card-pad">
          {series === null ? (
            <div className="skeleton" style={{ height: "200px" }} />
          ) : windowTotal === 0 ? (
            <p className="muted" style={{ textAlign: "center", padding: "48px 20px" }}>
              No API requests in the last {range} days yet. Once this org starts making requests, daily traffic
              shows up here.
            </p>
          ) : (
            <AreaChart values={values} labels={labels} height={200} />
          )}
        </div>
      </div>

      {/* Per-key usage (this month) — keys are not linked to the day range above. */}
      <div className="card mt rise">
        <div className="card-head">
          <div className="row gap-sm">
            <h2>Key usage</h2>
            <span className="badge badge-neutral mono">{keyUsage?.period ?? period}</span>
          </div>
          <select
            className="input"
            style={{ maxWidth: 160, padding: "4px 8px", fontSize: 13 }}
            value={endpointFilter}
            onChange={(e) => setEndpointFilter(e.target.value as UsageEndpoint | "all")}
            aria-label="Filter by endpoint"
          >
            <option value="all">All endpoints</option>
            {(["convert", "rates", "ppp", "ai"] as UsageEndpoint[]).map((ep) => (
              <option key={ep} value={ep}>{ep}</option>
            ))}
          </select>
        </div>
        <div className="card-pad">
          {(() => {
            if (keyUsage === null) {
              return <div className="skeleton" style={{ height: "120px" }} />;
            }
            // Focused single-endpoint view: only keys that hit that endpoint,
            // ordered by that endpoint's volume.
            const focus = endpointFilter !== "all" ? endpointFilter : null;
            const rows = focus
              ? keyUsage.keys
                  .filter((k) => k.byEndpoint[focus] > 0)
                  .sort((a, b) => b.byEndpoint[focus] - a.byEndpoint[focus])
              : keyUsage.keys;
            if (rows.length === 0) {
              return (
                <p className="muted" style={{ textAlign: "center", padding: "32px 20px" }}>
                  {focus
                    ? `No ${focus} requests on any key this month.`
                    : "No per-key activity yet this month. Requests made with your live and test keys show up here."}
                </p>
              );
            }
            return (
            <table className="data" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Env</th>
                  <th style={{ textAlign: "right" }}>Requests</th>
                  {focus ? (
                    <th style={{ textAlign: "right" }}>{focus}</th>
                  ) : (
                    <>
                      {(["convert", "rates", "ai"] as UsageEndpoint[]).map((ep) => (
                        <th key={ep} style={{ textAlign: "right" }}>{ep}</th>
                      ))}
                      <th style={{ textAlign: "right" }}>other</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((k) => (
                  <tr key={k.keyId}>
                    <td className="strong">{k.name ?? <span className="muted">deleted key</span>}</td>
                    <td>
                      <span className={`badge ${k.environment === "live" ? "badge-accent" : "badge-neutral"}`}>
                        {k.environment}
                      </span>
                    </td>
                    <td className="tnum" style={{ textAlign: "right" }}>{fmt(k.requests)}</td>
                    {focus ? (
                      <td className="tnum" style={{ textAlign: "right" }}>{fmt(k.byEndpoint[focus])}</td>
                    ) : (
                      <>
                        {(["convert", "rates", "ai"] as UsageEndpoint[]).map((ep) => (
                          <td key={ep} className="tnum" style={{ textAlign: "right" }}>{fmt(k.byEndpoint[ep])}</td>
                        ))}
                        <td className="tnum" style={{ textAlign: "right" }}>
                          {fmt(Math.max(0, k.requests - k.byEndpoint.convert - k.byEndpoint.rates - k.byEndpoint.ai))}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            );
          })()}
        </div>
      </div>


      {/* Current plan */}
      <div className="card card-pad mt rise">
        <div className="between">
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "22px", fontWeight: 600 }}>{usage.planLabel}</div>
            <div className="muted" style={{ fontSize: "12.5px" }}>
              {unlimited ? "Unlimited" : fmt(effLimit!)} requests / month
              {usage.grantRequests > 0 && <> (incl. +{fmt(usage.grantRequests)} grant)</>}
              {" · "}
              {aiLimit == null ? "unlimited" : fmt(aiLimit)} AI credits / month
            </div>
            <div className="muted" style={{ fontSize: "12.5px", marginTop: "2px" }}>
              {seatLimit == null ? "Unlimited" : seatLimit} team seats · {seatsUsed} used
              {cycleStart && <> · billing cycle {cycleStart}{cycleEnd ? ` → ${cycleEnd}` : ""}</>}
            </div>
          </div>
          <div className="row gap-sm">
            {usage.planKind === "custom" && <span className="badge badge-warn">Custom</span>}
            <Link to={`/dashboard/${orgId}/plans#ai-credits`} className="btn">Purchase credits</Link>
            <Link to={`/dashboard/${orgId}/billing`} className="btn btn-primary">Upgrade</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
